import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import StageShell from "@/components/StageShell";
import { useRun } from "@/hooks/useRun";
import { api, errMsg } from "@/lib/api";

function AngleCard({ a, i, chosen, titles, onChoose }) {
  const [angle, setAngle] = useState(a.angle);
  const [title, setTitle] = useState(a.headline_title);
  const active = chosen === i;
  return (
    <div data-testid={`positioning-angle-card-${i}`}
      className={`card-surface flex flex-col gap-3 transition-colors ${active ? "border-blue-500 ring-2 ring-blue-500/30" : "hover:border-slate-600"}`}>
      <div className="label-xs">Angle {i + 1}</div>
      <textarea value={angle} onChange={(e) => setAngle(e.target.value)} data-testid={`positioning-angle-text-${i}`}
        className="bg-transparent text-lg font-display font-semibold leading-snug outline-none resize-none min-h-[72px] focus:bg-slate-900 rounded p-1" />
      <label className="text-xs text-muted-foreground">Headline title (from title map)
        <select value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full bg-slate-900 border border-border rounded-md px-2 py-1.5 text-sm text-slate-100" data-testid={`positioning-title-${i}`}>
          {[...new Set([a.headline_title, ...titles])].map((t) => <option key={t}>{t}</option>)}
        </select>
      </label>
      {!a.in_title_map && <div className="text-xs text-amber-300 flex gap-1"><AlertTriangle size={12} /> AI title is not in the title map, so pick one from the list</div>}
      <div className="flex flex-wrap gap-1">{a.core_skills.map((s) => <span key={s} className="text-xs px-2 py-0.5 rounded bg-slate-800">{s}</span>)}</div>
      <p className="text-sm text-slate-400 flex-1">{a.rationale}</p>
      <button data-testid={`positioning-choose-${i}`} onClick={() => onChoose(i, angle, title)}
        className={`rounded-lg py-2 text-sm font-medium transition-colors ${active ? "bg-blue-600 text-white" : "bg-slate-800 hover:bg-slate-700"}`}>
        {active ? <span className="inline-flex items-center gap-1"><CheckCircle2 size={14} /> Chosen for this track</span> : "Use this angle & write the draft"}
      </button>
    </div>
  );
}

export default function Stage4({ job, refresh }) {
  const runHook = useRun(job.id, 4, () => refresh());
  const p = job.positioning;
  const titles = (job.market?.title_map || []).map((t) => t.title);
  const choose = async (index, angle, headline_title) => {
    try {
      await api.post(`/jobs/${job.id}/positioning/choose`, { index, angle, headline_title });
      await api.post(`/jobs/${job.id}/stages/4/approve`);
      toast.success("Angle saved. The AI is writing the draft now");
      refresh(true);
    } catch (e) { toast.error(errMsg(e)); refresh(); }
  };
  return (
    <StageShell job={job} stage={4} refresh={refresh} runHook={runHook}
      runs={[{ action: "positioning", label: p ? "Propose new angles" : "Propose angles", testid: "positioning-run-button" }]}>
      <div className="text-sm text-muted-foreground">Track: <span className="text-slate-200">{job.track_title}</span>. Pick one angle for this track. {job.sibling_jobs.length > 1 && "The other track has its own resume job (see tabs above)."}</div>
      {p ? (
        <div className="grid grid-cols-3 gap-5">
          {p.angles.map((a, i) => <AngleCard key={`${p.at}-${i}`} a={a} i={i} chosen={p.chosen} titles={titles} onChoose={choose} />)}
        </div>
      ) : <div className="card-surface text-sm text-muted-foreground">Click "Propose angles" to get 3 positioning options.</div>}
    </StageShell>
  );
}
